import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Headphones, BookOpen, Mic, PenTool, Clock, ListChecks,
  Lightbulb, CheckCircle2, Target, Sparkles, ChevronRight
} from 'lucide-react';
import { DarkModeToggle } from '../components/DarkModeToggle';
import { settingsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const modules = [
  {
    id: 'comprehension-orale',
    title: 'Compréhension Orale',
    subtitle: 'Écouter et comprendre des documents audio authentiques',
    icon: Headphones,
    color: 'bg-green-500',
    ring: 'ring-green-500/20',
    text: 'text-green-600',
    bg: 'bg-green-500/10',
    format: [
      { label: 'Questions', value: '39 QCM' },
      { label: 'Durée', value: '35 minutes' },
      { label: 'Écoute', value: 'Une seule fois par question' },
      { label: 'Niveaux', value: 'A1 → C2 (difficulté progressive)' },
    ],
    structure: [
      'De courts échanges de la vie quotidienne (annonces, conversations, messages).',
      'Des documents plus longs (interviews, reportages, émissions de radio).',
      'La difficulté augmente au fil du test : les premières questions sont simples, les dernières exigent une compréhension fine (implicite, ton, nuances).',
    ],
    method: [
      "Lisez la question et les 4 propositions AVANT le début de l'écoute pour savoir quoi repérer.",
      "Concentrez-vous sur les mots-clés (qui, quoi, où, quand, pourquoi) dès la première écoute.",
      "Ne vous bloquez pas sur un mot inconnu : gardez le fil du sens général.",
      "Répondez tout de suite après l'écoute, la mémoire auditive s'efface vite.",
      "En cas de doute, éliminez d'abord les réponses clairement fausses.",
    ],
    tips: [
      "Entraînez l'oreille quotidiennement avec des podcasts et journaux radio en français.",
      "Travaillez la prise de notes rapide (abréviations, symboles) pour les documents longs.",
      "Familiarisez-vous avec les accents et débits de parole variés (France, Québec, Afrique francophone).",
    ],
    path: '/modules/comprehension-orale',
  },
  {
    id: 'comprehension-ecrite',
    title: 'Compréhension Écrite',
    subtitle: 'Lire et analyser des documents écrits variés',
    icon: BookOpen,
    color: 'bg-blue-500',
    ring: 'ring-blue-500/20',
    text: 'text-blue-600',
    bg: 'bg-blue-500/10',
    format: [
      { label: 'Questions', value: '39 QCM' },
      { label: 'Durée', value: '60 minutes' },
      { label: 'Supports', value: 'Annonces, emails, articles, extraits littéraires' },
      { label: 'Niveaux', value: 'A1 → C2 (difficulté progressive)' },
    ],
    structure: [
      "Des textes courts et fonctionnels (affiches, petites annonces, SMS, emails).",
      "Des textes informatifs et argumentatifs plus longs (articles de presse, chroniques).",
      "Comme à l'oral, la difficulté croît progressivement jusqu'à des textes littéraires ou abstraits.",
    ],
    method: [
      "Repérez d'abord le type de texte et sa source (titre, mise en page, émetteur) : cela oriente la compréhension.",
      "Lisez la question avant le texte pour cibler directement l'information recherchée.",
      "Survolez le texte une première fois pour saisir l'idée générale, puis relisez en détail la partie utile.",
      "Méfiez-vous des pièges lexicaux : une réponse peut reprendre des mots du texte sans en être le sens correct.",
      "Gérez votre temps : environ 1 min 30 par question, ne restez pas bloqué sur une seule.",
    ],
    tips: [
      "Lisez régulièrement la presse francophone (Radio-Canada, Le Monde, La Presse) pour élargir le vocabulaire.",
      "Apprenez à reconnaître les connecteurs logiques (cependant, en revanche, par conséquent...).",
      "Chronométrez vos entraînements pour gagner en rapidité de lecture.",
    ],
    path: '/modules/comprehension-ecrite',
  },
  {
    id: 'expression-orale',
    title: 'Expression Orale',
    subtitle: "S'exprimer clairement face à l'ordinateur, sans interlocuteur",
    icon: Mic,
    color: 'bg-orange-500',
    ring: 'ring-orange-500/20',
    text: 'text-orange-600',
    bg: 'bg-orange-500/10',
    format: [
      { label: 'Tâches', value: '3 tâches enregistrées' },
      { label: 'Durée', value: '12 minutes au total' },
      { label: 'Préparation', value: 'Temps de préparation court avant chaque tâche' },
      { label: 'Évaluation', value: 'Note de 0 à 20 par tâche' },
    ],
    structure: [
      "Tâche 1 — Se présenter et répondre à des questions personnelles simples (identité, goûts, habitudes).",
      "Tâche 2 — Poser des questions à partir d'une situation donnée pour obtenir une information précise (jeu de rôle inversé).",
      "Tâche 3 — Exprimer et défendre une opinion argumentée sur un sujet de société.",
    ],
    method: [
      "Parlez d'une voix posée et articulez, même sous la pression du chronomètre.",
      "Structurez toujours votre réponse : introduction, développement, conclusion.",
      "Pour la tâche 2, posez des questions variées (fermées et ouvertes) sans réciter une liste.",
      "Pour la tâche 3, prenez clairement position et justifiez avec des exemples concrets.",
      "N'ayez pas peur des silences courts pour réfléchir : mieux vaut une phrase correcte qu'une phrase précipitée.",
    ],
    tips: [
      "Enregistrez-vous à l'entraînement et réécoutez-vous pour corriger prononciation et rythme.",
      "Constituez une liste de connecteurs à l'oral (d'abord, ensuite, de plus, en conclusion...).",
      "Entraînez-vous à parler 1 à 2 minutes sans interruption sur des sujets variés.",
    ],
    path: '/modules/expression-orale',
  },
  {
    id: 'expression-ecrite',
    title: 'Expression Écrite',
    subtitle: 'Rédiger des textes clairs, structurés et argumentés',
    icon: PenTool,
    color: 'bg-purple-500',
    ring: 'ring-purple-500/20',
    text: 'text-purple-600',
    bg: 'bg-purple-500/10',
    format: [
      { label: 'Tâches', value: '3 productions écrites' },
      { label: 'Durée', value: '60 minutes au total' },
      { label: 'Longueur', value: 'De ~60 mots à ~180 mots selon la tâche' },
      { label: 'Évaluation', value: 'Note de 0 à 20 par tâche' },
    ],
    structure: [
      "Tâche 1 — Rédiger un message court et fonctionnel (invitation, message informel, annonce).",
      "Tâche 2 — Relater un événement ou exprimer un sentiment / une opinion sur un sujet donné.",
      "Tâche 3 — Analyser un document (graphique, statistique, citation) et défendre un point de vue argumenté.",
    ],
    method: [
      "Respectez toujours le nombre de mots demandé : trop court pénalise, trop long fait perdre du temps.",
      "Planifiez 2 minutes avant d'écrire : idées principales, plan, conclusion.",
      "Variez le vocabulaire et évitez les répétitions, utilisez des synonymes.",
      "Soignez les connecteurs logiques et la ponctuation pour une lecture fluide.",
      "Réservez toujours 3 à 5 minutes de relecture pour corriger orthographe, accords et conjugaison.",
    ],
    tips: [
      "Utilisez notre correcteur IA pour recevoir un retour détaillé sur vos productions.",
      "Mémorisez des structures argumentatives réutilisables (d'une part... d'autre part, certes... cependant).",
      "Entraînez-vous régulièrement sur des sujets d'actualité et d'immigration, thèmes fréquents au TCF Canada.",
    ],
    path: '/modules/expression-ecrite',
  },
];

const generalTips = [
  {
    icon: Clock,
    title: 'Gérez votre temps',
    description: "Chaque épreuve est chronométrée. Entraînez-vous toujours dans les conditions réelles pour apprendre à doser votre temps par question ou par tâche.",
  },
  {
    icon: ListChecks,
    title: 'Progressez par niveau',
    description: "Le TCF Canada évalue du niveau A1 à C2. Travaillez chaque compétence en partant de vos points faibles identifiés dans votre tableau de bord.",
  },
  {
    icon: Target,
    title: 'Visez le score NCLC dont vous avez besoin',
    description: "Identifiez le NCLC requis pour votre programme d'immigration (ex : Entrée express, Arrima) et concentrez votre préparation sur ce seuil.",
  },
  {
    icon: Sparkles,
    title: 'Pratiquez régulièrement',
    description: "La régularité prime sur l'intensité : quelques séries chaque semaine, dans les 4 compétences, donnent de meilleurs résultats qu'une révision de dernière minute.",
  },
];

export default function Methodologie() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [siteSettings, setSiteSettings] = useState({});

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await settingsAPI.getSiteSettings();
        setSiteSettings(settings || {});
      } catch (error) {
        console.error('Error loading site settings:', error);
      }
    };
    loadSettings();
  }, []);

  const code = searchParams.get('code');

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate(code ? `/?code=${code}` : '/')}
                className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
                <span className="hidden sm:inline">Retour</span>
              </button>
              <div className="h-6 w-px bg-border" />
              {siteSettings.logo ? (
                <img
                  src={siteSettings.logo.startsWith('http') ? siteSettings.logo : `${API_URL}${siteSettings.logo.startsWith('/') ? '' : '/'}${siteSettings.logo}`}
                  alt="Logo"
                  className="h-8 w-auto"
                />
              ) : null}
              <h1 className="text-xl sm:text-2xl font-bold text-primary">
                {siteSettings.site_name || 'TCF Canada'}
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <DarkModeToggle />
              {user ? (
                <button
                  onClick={() => navigate('/dashboard')}
                  data-testid="nav-dashboard-button"
                  className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-medium text-sm"
                >
                  Mon espace
                </button>
              ) : (
                <button
                  onClick={() => navigate(code ? `/login?code=${code}` : '/login')}
                  data-testid="nav-login-button"
                  className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-medium text-sm"
                >
                  Se connecter
                </button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-6">
            <Sparkles className="h-4 w-4" />
            <span className="text-sm font-medium">Guide officiel de préparation</span>
          </div>
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold mb-4">
            La Méthodologie du TCF Canada
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Comprenez le format exact de chacune des 4 épreuves, la stratégie à adopter question par
            question, et les astuces de nos formateurs pour maximiser votre score NCLC.
          </p>
        </div>
      </section>

      {/* Modules */}
      <section className="pb-8 px-4">
        <div className="max-w-6xl mx-auto space-y-10">
          {modules.map((mod) => {
            const Icon = mod.icon;
            return (
              <div
                key={mod.id}
                id={mod.id}
                className="bg-card border rounded-2xl shadow-sm overflow-hidden scroll-mt-24"
                data-testid={`methodologie-${mod.id}`}
              >
                {/* Header */}
                <div className={`p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 ${mod.bg}`}>
                  <div className="flex items-center space-x-4">
                    <div className={`h-14 w-14 rounded-xl ${mod.color} flex items-center justify-center shadow-sm flex-shrink-0`}>
                      <Icon className="h-7 w-7 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl sm:text-2xl font-bold">{mod.title}</h2>
                      <p className="text-sm text-muted-foreground">{mod.subtitle}</p>
                    </div>
                  </div>
                  <Link
                    to={code ? `${mod.path}?code=${code}` : mod.path}
                    className={`inline-flex items-center justify-center space-x-2 px-5 py-2.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-medium text-sm whitespace-nowrap`}
                  >
                    <span>S'entraîner</span>
                    <ChevronRight className="h-4 w-4" />
                  </Link>
                </div>

                <div className="p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* Format */}
                  <div>
                    <h3 className="flex items-center space-x-2 font-semibold mb-4">
                      <Clock className={`h-4 w-4 ${mod.text}`} />
                      <span>Format de l'épreuve</span>
                    </h3>
                    <dl className="space-y-3">
                      {mod.format.map((item, i) => (
                        <div key={i} className="flex items-start justify-between text-sm gap-3">
                          <dt className="text-muted-foreground">{item.label}</dt>
                          <dd className="font-medium text-right">{item.value}</dd>
                        </div>
                      ))}
                    </dl>

                    <h3 className="flex items-center space-x-2 font-semibold mt-6 mb-4">
                      <ListChecks className={`h-4 w-4 ${mod.text}`} />
                      <span>Structure</span>
                    </h3>
                    <ul className="space-y-2">
                      {mod.structure.map((s, i) => (
                        <li key={i} className="text-sm text-muted-foreground flex items-start space-x-2">
                          <span className={`mt-1.5 h-1.5 w-1.5 rounded-full ${mod.color} flex-shrink-0`} />
                          <span>{s}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Method */}
                  <div>
                    <h3 className="flex items-center space-x-2 font-semibold mb-4">
                      <Target className={`h-4 w-4 ${mod.text}`} />
                      <span>Méthode pas à pas</span>
                    </h3>
                    <ol className="space-y-3">
                      {mod.method.map((m, i) => (
                        <li key={i} className="text-sm flex items-start space-x-3">
                          <span className={`h-5 w-5 rounded-full ${mod.bg} ${mod.text} flex items-center justify-center text-xs font-bold flex-shrink-0`}>
                            {i + 1}
                          </span>
                          <span className="text-muted-foreground">{m}</span>
                        </li>
                      ))}
                    </ol>
                  </div>

                  {/* Tips */}
                  <div>
                    <h3 className="flex items-center space-x-2 font-semibold mb-4">
                      <Lightbulb className={`h-4 w-4 ${mod.text}`} />
                      <span>Astuces de préparation</span>
                    </h3>
                    <ul className="space-y-3">
                      {mod.tips.map((t, i) => (
                        <li key={i} className="text-sm flex items-start space-x-2">
                          <CheckCircle2 className={`h-4 w-4 ${mod.text} flex-shrink-0 mt-0.5`} />
                          <span className="text-muted-foreground">{t}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* General tips */}
      <section className="py-16 px-4 bg-secondary/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">Conseils généraux pour réussir</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Une bonne méthode s'applique aux 4 épreuves. Voici les principes que suivent nos candidats
              qui obtiennent les meilleurs scores.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {generalTips.map((tip, i) => {
              const Icon = tip.icon;
              return (
                <div key={i} className="bg-card p-6 rounded-xl border shadow-sm">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-2">{tip.title}</h3>
                  <p className="text-sm text-muted-foreground">{tip.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center bg-gradient-to-r from-primary to-primary/70 rounded-2xl p-10 text-primary-foreground">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3">Prêt à mettre cette méthode en pratique ?</h2>
          <p className="text-primary-foreground/90 mb-6 max-w-xl mx-auto">
            Accédez à des centaines de questions dans les conditions réelles de l'examen et suivez votre
            progression épreuve par épreuve.
          </p>
          <button
            onClick={() => navigate(user ? '/dashboard' : (code ? `/tarification?code=${code}` : '/tarification'))}
            data-testid="methodologie-cta-button"
            className="inline-flex items-center space-x-2 px-6 py-3 rounded-lg bg-white text-primary hover:bg-white/90 transition-colors font-semibold"
          >
            <span>{user ? 'Aller à mon tableau de bord' : 'Voir les formules'}</span>
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </section>
    </div>
  );
}
